'use client'

import Image from 'next/image'
import { AnimatePresence, animate, motion, useInView, useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { ItemsShelf } from '@/components/items/items-shelf'
import type { OverviewItem } from '@/lib/items-overview'
import { cn } from '@/lib/utils'
import { EASE } from './animation-tokens'


/** Runs `steps` once, in order, after the element scrolls into view. */
function useSequence(inView: boolean, steps: number[]) {
  const reduceMotion = useReducedMotion()
  const [step, setStep] = useState(0)
  useEffect(() => {
    if (!inView) return
    if (reduceMotion) {
      setStep(steps.length)
      return
    }
    const timers = steps.map((at, i) => window.setTimeout(() => setStep(i + 1), at))
    return () => timers.forEach(window.clearTimeout)
    // steps is a literal per call site
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, reduceMotion])
  return step
}

function Notification({ title, body, when }: { title: string; body: string; when: string }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-lg shadow-foreground/10">
      <Image src="/favicons/haven-rounded.png" alt="" width={36} height={36} className="rounded-lg" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-semibold text-foreground">{title}</p>
          <span className="text-xs text-muted-foreground">{when}</span>
        </div>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  )
}

/**
 * "Reminders that don't quit": yesterday's reminder, then today's arrives on top
 * of it, because the job still isn't done.
 */
export function ReminderStack({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.6 })
  const step = useSequence(inView, [200, 1600])

  return (
    <div ref={ref} aria-hidden className={cn('relative', className)}>
      <motion.div
        initial={false}
        animate={
          step === 0
            ? { opacity: 0, y: -16 }
            : step === 1
              ? { opacity: 1, y: 0, scale: 1 }
              : { opacity: 0.7, y: 14, scale: 0.94 }
        }
        transition={{ duration: 0.5, ease: EASE }}
      >
        <Notification title="Replace furnace filter" body="6 days late. You have 1 filter left in the garage." when="Yesterday" />
      </motion.div>
      <AnimatePresence>
        {step >= 2 && (
          <motion.div
            className="absolute inset-x-0 top-0"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <Notification title="Replace furnace filter" body="7 days late. Still on your list until it's done." when="now" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/** "The exact part, before you run out": the count ticks down to zero, then the reorder button lights up. */
export function PartStockCallout({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.6 })
  const step = useSequence(inView, [1200, 2600, 3600])
  const count = [2, 1, 0, 0][step]
  const out = count === 0

  return (
    <div
      ref={ref}
      aria-hidden
      className={cn('rounded-2xl border border-border bg-card p-4 shadow-lg shadow-foreground/10', className)}
    >
      <div className="flex items-center gap-3">
        <Image src="/landing/app/hvac-filter.webp" alt="" width={48} height={48} className="size-12 rounded-lg object-cover" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground">HVAC filter</p>
          <p className="text-sm text-muted-foreground">14x16x1, Amazon</p>
        </div>
        <p className="text-sm text-muted-foreground">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={count}
              className={cn('inline-block text-lg font-semibold tabular-nums', out ? 'text-destructive' : 'text-foreground')}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              {count}
            </motion.span>
          </AnimatePresence>{' '}
          left
        </p>
      </div>
      <div
        className={cn(
          'mt-3 rounded-full border py-1.5 text-center text-sm transition-colors duration-500',
          step >= 3 ? 'border-primary bg-primary text-primary-foreground' : 'border-primary/60 text-primary'
        )}
      >
        Reorder from Amazon
      </div>
    </div>
  )
}

const item = (id: string, name: string, category: string, location: string): OverviewItem =>
  ({ id, name, category, location, imageUrl: `/landing/items/${id}.webp`, warrantyExpiry: null, purchaseDate: null }) as OverviewItem

// Demo items have no warranty or purchase dates, so the shelf subtitle never depends on it.
const DEMO_TODAY = '2026-09-29'

const KITCHEN = [
  item('breville', 'Breville Barista Pro', 'Espresso machine', 'Kitchen'),
  item('dishwasher', 'Bosch 800 Series dishwasher', 'Dishwasher', 'Kitchen'),
  item('fridge', 'Samsung French-door fridge', 'Refrigerator', 'Kitchen'),
  item('range', 'Bosch slide-in range', 'Range', 'Kitchen'),
  item('hood', 'Stainless range hood', 'Range hood', 'Kitchen'),
  item('faucet', 'Moen kitchen faucet', 'Plumbing', 'Kitchen'),
  item('mixer', 'KitchenAid stand mixer', 'Small appliance', 'Kitchen'),
  item('kettle', 'Smeg electric kettle', 'Small appliance', 'Kitchen'),
  item('toaster', 'Dualit toaster', 'Small appliance', 'Kitchen'),
]
const GARAGE = [
  item('mower', 'Honda HRX lawn mower', 'Outdoor equipment', 'Garage'),
  item('civic', 'Honda Civic', 'Vehicle', 'Garage'),
  item('garage-door', 'Garage door', 'Exterior', 'Garage'),
  item('heater', 'Presto HeatDish heater', 'Heating', 'Garage'),
]

/**
 * The app's own room shelves, drifting sideways as if someone is browsing. The
 * page's only continuous motion: it runs while in view and stops under reduced motion.
 */
export function ItemsShelves({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.3 })
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    const root = ref.current
    if (!root || !inView || reduceMotion) return
    const shelves = [...root.querySelectorAll('ul')]
    const controls = shelves.map((ul, i) => {
      const max = ul.scrollWidth - ul.clientWidth
      if (max <= 0) return null
      const from = i % 2 === 0 ? 0 : max
      return animate(from, max - from, {
        duration: 14 + i * 3,
        ease: 'easeInOut',
        repeat: Infinity,
        repeatType: 'mirror',
        repeatDelay: 1.2,
        onUpdate: (v) => {
          ul.scrollLeft = v
        },
      })
    })
    return () => controls.forEach((c) => c?.stop())
  }, [inView, reduceMotion])

  return (
    <div ref={ref} aria-hidden inert className={cn('[&_ul]:snap-none [&_li]:w-40', className)}>
      <ItemsShelf title="Kitchen" count={KITCHEN.length} items={KITCHEN} today={DEMO_TODAY} onSelect={() => {}} onSeeAll={() => {}} />
      <ItemsShelf title="Garage" count={GARAGE.length} items={GARAGE} today={DEMO_TODAY} onSelect={() => {}} onSeeAll={() => {}} />
    </div>
  )
}
